
import React, { useEffect, useState } from 'react';
import Navbar from './landingpage-component/Navbar';
import Hero from './landingpage-component/Hero';
import Problem from './landingpage-component/Problem';
import SolutionFlow from './landingpage-component/SolutionFlow';
import FeaturesGrid from './landingpage-component/FeaturesGrid';
import DemoWhatsApp from './landingpage-component/DemoWhatsApp';
import CompareTable from './landingpage-component/CompareTable';
import AskMarco from './landingpage-component/AskMarco';
import LogoStats from './landingpage-component/LogoStats';
import FinalCTA from './landingpage-component/FinalCTA';
import Footer from './landingpage-component/Footer';
import { X } from 'lucide-react';
// import { useToast } from './hooks/use-toast';
// import { Toaster } from './landingpage-component/ui/toast';
import { ThemeProvider } from './context/ThemeContext';


function LandingPage() {
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [exitIntentShown, setExitIntentShown] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  // const { toasts, dismiss } = useToast();
  
  // Handle exit intent popup
  useEffect(() => {
    const handleMouseLeave = (e) => {
      // Only trigger if moving cursor above the top of the viewport
      if (e.clientY <= 0 && !exitIntentShown && window.scrollY > window.innerHeight * 0.7) {
        setShowExitIntent(true);
        setExitIntentShown(true);  // Only show once per session
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [exitIntentShown]);
  
  // Handle CTA pulse animation using Intersection Observer
  useEffect(() => {
    let observer;
    const ctaElements = document.querySelectorAll('.cta-primary');
    
    if (ctaElements.length > 0) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const cta = entry.target;
            
            // Add the pulse animation after a slight delay
            setTimeout(() => {
              cta.classList.add('animate-pulse-cta');
              
              // Remove the animation class after it completes
              setTimeout(() => {
                cta.classList.remove('animate-pulse-cta');
              }, 900);
            }, 500);
            
            // Unobserve after first animation
            observer.unobserve(cta);
          }
        });
      }, { threshold: 0.7 });
      
      ctaElements.forEach(cta => observer.observe(cta));
    }
    
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);
  
  // Setup Lottie animation
  useEffect(() => {
    // This would normally load the Lottie library and animation
    // For now we'll just log it would happen
    console.log('Lottie animation would load here');
    
    // Example of what actual Lottie code would look like
    /*
    import lottie from 'lottie-web';
    lottie.loadAnimation({
      container: document.getElementById('heroLottie'),
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: '/public/hero-flow.lottie'
    });
    */
  }, []);
  
  // Smooth scroll implementation
  useEffect(() => {
    const handleAnchorClick = (e) => {
      const target = e.target ;
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const id = target.getAttribute('href')?.substring(1);
        const element = document.getElementById(id || '');
        if (element) {
          window.scrollTo({
            top: element.offsetTop - 80, // Adjust for navbar height
            behavior: 'smooth'
          });
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);

  // Handle WhatsApp exit intent submission
  const handleExitIntentSubmit = (e) => {
    e.preventDefault();
    if (!whatsappNumber) return;
    
    console.log('Exit intent form submitted with number:', whatsappNumber);
    // Here you would normally make an API call
    
    // Close the popup
    setShowExitIntent(false);
  };

  return (
    <ThemeProvider>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main>
          <Hero />
          <Problem />
          <SolutionFlow />
          <FeaturesGrid />
          {/* <DemoWhatsApp /> */}
          <CompareTable />
          {/* <AskMarco /> */}
          {/* <LogoStats /> */}
          {/* <FinalCTA /> */}
        </main>
        <Footer />
        
        {/* Toast Notifications - Temporarily disabled */}
        {/* <Toaster toasts={toasts} dismiss={dismiss} /> */}
        
        {/* Exit Intent Popup */}
      {showExitIntent && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative animate-fade-up">
            <button 
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" 
              onClick={() => setShowExitIntent(false)}
            >
              <X size={20} />
            </button>
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold mb-2">Want Marco to message you?</h3>
              <p className="text-gray-600">Enter your WhatsApp number and our AI assistant will reach out with more info.</p>
            </div>
            <form onSubmit={handleExitIntentSubmit}>
              <input
                type="tel"
                placeholder="+971 XX XXX XXXX"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-primary/50"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                required
              />
              <button 
                type="submit" 
                className="btn-primary w-full"
              >
                Send Me More Info
              </button>
              <p className="text-xs text-center text-gray-500 mt-3">
                We'll send you a single message. No spam, promise!
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Global CSS for CTA shimmer effect */}
      <style>
        {`
        .shimmer-overlay {
          background: linear-gradient(
            120deg, 
            rgba(255,255,255,0) 0%, 
            rgba(255,255,255,.15) 50%, 
            rgba(255,255,255,0) 100%
          );
          transform: translateX(-100%);
        }
        
        .btn-primary:hover .shimmer-overlay {
          animation: shimmer 2s infinite;
        }
        `}
      </style>
      </div>
    </ThemeProvider>
  );
}

export default LandingPage